import { useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSButton from "views/system-design/DSButton";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSAlert from "views/system-design/DSAlert";
import DSChip from "views/system-design/DSChip";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PageHeader from "components/PageHeader";
import useTabParam from "components/hooks/useTabParam";
import { extractApiErrorMessage } from "../../../utils/util";
import {
  useContentProfile,
  useMarkContentProfileReady,
  useSyncContentProfile,
} from "../hooks/useContentProfiles";
import { useProfileDraft } from "../hooks/useProfileDraft";
import { profileStatus } from "../utils/profileStatus";
import DevicesTab from "./DevicesTab";
import SettingsTab from "./SettingsTab";

const TABS = ["devices", "settings"] as const;

const ContentProfileDetail = () => {
  const { id = "" } = useParams<{ id: string }>();
  const [tabParam, setTabParam] = useTabParam("tab", "settings", TABS);
  const { data: profile, isLoading, isError, error } = useContentProfile(id);
  const draft = useProfileDraft(profile);

  const [confirmingSync, setConfirmingSync] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // The API's message when the order's clock count does not match
  // (error CLOCK_COUNT_MISMATCH); the admin confirms it to push to the clocks
  // the order has.
  const [clockMismatch, setClockMismatch] = useState<string | null>(null);
  const { mutate: sync, isPending: isSyncing } = useSyncContentProfile();
  const [confirmingReady, setConfirmingReady] = useState(false);
  const { mutate: markReady, isPending: isMarkingReady } =
    useMarkContentProfileReady();

  const handleMarkReady = () => {
    markReady(id, {
      onSettled: () => setConfirmingReady(false),
      onError: (e) =>
        setNotice(extractApiErrorMessage(e, "Failed to mark ready")),
    });
  };

  const handleSync = (pushDespiteClockMismatch = false) => {
    sync(
      { id, pushDespiteClockMismatch },
      {
        onSuccess: ({ sent, failed }) => {
          setConfirmingSync(false);
          setClockMismatch(null);
          setNotice(
            failed > 0
              ? `Pushed to ${sent} device${sent === 1 ? "" : "s"}. ${failed} failed — you can safely retry.`
              : `Pushed to ${sent} device${sent === 1 ? "" : "s"}.`,
          );
        },
        onError: (e) => {
          setConfirmingSync(false);
          setClockMismatch(null);
          const message = extractApiErrorMessage(e, "Sync failed");
          if (
            axios.isAxiosError(e) &&
            e.response?.data?.error === "CLOCK_COUNT_MISMATCH"
          ) {
            setClockMismatch(message);
          } else {
            setNotice(message);
          }
        },
      },
    );
  };

  if (isLoading) {
    return (
      <DSBox sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (isError || !profile) {
    return (
      <DSBox sx={{ p: 2 }}>
        <DSTypography color="error">
          Failed to load profile:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </DSTypography>
      </DSBox>
    );
  }

  const status = profileStatus(profile);
  // Mirrors the API's markReady checks, so the button explains itself
  // instead of failing.
  const readyBlocker = draft.dirty
    ? "Save your settings changes first"
    : !profile.orderName && !profile.clockIds.length
      ? "Add devices first"
      : !Object.keys(profile.values).length &&
          !profile.machineCollections.length &&
          !profile.favorites.length
        ? "Nothing to push yet"
        : null;

  return (
    <DSBox>
      <PageHeader
        overline="content-profile"
        title={profile.name}
        subtitle={
          profile.orderName
            ? `Targets order ${profile.orderName}: pushed to the order's clocks automatically once it has them`
            : "Editing settings here changes nothing on clocks until you press Sync"
        }
        tabs={
          <DSSegmentedTabs
            options={[
              { label: "Settings", value: "settings" },
              { label: "Devices", value: "devices" },
            ]}
            value={tabParam ?? "devices"}
            onChange={(value) => setTabParam(value)}
          />
        }
        actions={
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DSChip
              size="small"
              color={status.color}
              label={status.label}
              icon={status.locked ? <LockOutlinedIcon /> : undefined}
            />
            {!status.locked && (
              <>
                {readyBlocker && (
                  <DSTypography variant="caption" color="text.secondary">
                    {readyBlocker}
                  </DSTypography>
                )}
                <DSButton
                  variant="contained"
                  size="small"
                  disabled={!!readyBlocker}
                  onClick={() => setConfirmingReady(true)}
                >
                  Mark ready
                </DSButton>
              </>
            )}
            {/* An order profile is pushed by the API's order trigger; Sync
                only retries what failed: the whole push, or the devices
                the submission missed. */}
            {status.locked &&
              (!profile.orderName ||
                !profile.submittedAt ||
                !!status.failedDevices) && (
                <DSButton
                  variant="contained"
                  size="small"
                  disabled={!profile.orderName && !profile.clockIds.length}
                  onClick={() => setConfirmingSync(true)}
                >
                  {!profile.orderName
                    ? "Sync"
                    : status.failedDevices
                      ? "Retry failed devices"
                      : "Retry push"}
                </DSButton>
              )}
          </DSBox>
        }
      />

      {!status.locked && (
        <DSAlert severity="warning" sx={{ mb: 2 }}>
          Draft: ignored until you mark it ready. Nothing is pushed
          {profile.orderName
            ? ". The order's gift and bundle go to its clocks without it. Marking it ready after that replaces the bundle with the profile's collections."
            : "."}
        </DSAlert>
      )}
      {status.locked && (
        <DSAlert severity="info" icon={<LockOutlinedIcon />} sx={{ mb: 2 }}>
          This profile is ready and locked. Its settings, collections, devices
          and gift can&apos;t change anymore.
        </DSAlert>
      )}

      {tabParam === "devices" && (
        <DevicesTab
          profileId={profile.id}
          orderName={profile.orderName}
          locked={status.locked}
        />
      )}
      {tabParam === "settings" && (
        <SettingsTab profile={profile} draft={draft} locked={status.locked} />
      )}

      <DSDialog
        open={confirmingReady}
        onClose={() => setConfirmingReady(false)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogTitle>Mark ready</DSDialogTitle>
        <DSDialogContent>
          <DSTypography variant="body2" sx={{ mb: 2 }}>
            {profile.orderName
              ? `The profile is pushed to order ${profile.orderName}'s clocks as soon as the order has all of them, or right away if it already does.`
              : "Once ready, you can push the profile to its devices with Sync."}
          </DSTypography>
          <DSAlert severity="warning">
            Settings, collections, devices and the gift are locked after this.
            This cannot be undone.
          </DSAlert>
        </DSDialogContent>
        <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
          <DSButton size="small" onClick={() => setConfirmingReady(false)}>
            Cancel
          </DSButton>
          <DSButton
            variant="contained"
            size="small"
            disabled={isMarkingReady}
            onClick={handleMarkReady}
          >
            Mark ready
          </DSButton>
        </DSDialogActions>
      </DSDialog>

      <DSDialog
        open={confirmingSync}
        onClose={() => setConfirmingSync(false)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogTitle>Sync profile</DSDialogTitle>
        <DSDialogContent>
          <DSTypography variant="body2" sx={{ mb: 2 }}>
            {profile.orderName
              ? `You are about to push this profile's settings, collections and favorites to order ${profile.orderName}'s clocks, overwriting whatever is there now. It works only once the order has all its clocks, or once the order is marked manual override.`
              : `You are about to push this profile's settings, collections and favorites to ${profile.clockIds.length} device${profile.clockIds.length === 1 ? "" : "s"}, overwriting whatever is there now.`}
          </DSTypography>
          <DSAlert severity="warning">
            This replaces each device's collections and favorites entirely —
            including anything a customer bought or redeemed outside this
            profile. Any manual changes made to these devices since the last
            sync will also be overwritten. This cannot be undone.
          </DSAlert>
        </DSDialogContent>
        <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
          <DSButton size="small" onClick={() => setConfirmingSync(false)}>
            Cancel
          </DSButton>
          <DSButton
            variant="contained"
            size="small"
            disabled={isSyncing}
            onClick={() => handleSync()}
          >
            Sync
          </DSButton>
        </DSDialogActions>
      </DSDialog>

      <DSDialog
        open={!!clockMismatch}
        // Closing while the push runs would look like a cancel that isn't.
        onClose={() => !isSyncing && setClockMismatch(null)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogTitle>Clock count mismatch</DSDialogTitle>
        <DSDialogContent>
          <DSTypography variant="body2" sx={{ mb: 2 }}>
            {clockMismatch}
          </DSTypography>
          <DSAlert severity="warning">
            Push anyway sends the profile to the clocks the order has now. Once
            at least one clock gets it, the profile is marked submitted, and
            clocks attached to the order later will not get it. This cannot be
            undone.
          </DSAlert>
        </DSDialogContent>
        <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
          <DSButton
            size="small"
            disabled={isSyncing}
            onClick={() => setClockMismatch(null)}
          >
            Cancel
          </DSButton>
          <DSButton
            variant="contained"
            size="small"
            disabled={isSyncing}
            onClick={() => handleSync(true)}
          >
            Push anyway
          </DSButton>
        </DSDialogActions>
      </DSDialog>

      <DSDialog
        open={!!notice}
        onClose={() => setNotice(null)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogContent>
          <DSTypography variant="body2">{notice}</DSTypography>
        </DSDialogContent>
        <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
          <DSButton
            variant="contained"
            size="small"
            onClick={() => setNotice(null)}
          >
            Close
          </DSButton>
        </DSDialogActions>
      </DSDialog>
    </DSBox>
  );
};

export default ContentProfileDetail;
